import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../contexts/AuthContext';
import axios from '../config/axios';
import { CheckIcon } from '@heroicons/react/24/outline';
import FAQSection from '../components/FAQSection';
import useSEO from '../hooks/useSEO';

const Pricing = () => {

  useSEO({
    title: 'SERPfinity Pricing - Affordable SEO & AEO Plans Starting at $17/month',
    description: 'SERPfinity pricing plans: Starter $17/mo, Business $39/mo, Enterprise $79/mo. All plans include AI Search Visibility tracking, SERP Analysis, and more.',
    canonical: 'https://app.serpfinity.ai/pricing',
    ogDescription: 'Affordable SEO and AEO tools for every budget. Choose your SERPfinity plan with free trial available.'
  });

  const { user } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleSubscribe = (planId) => {
    window.location.href = "/register";
  };

  const plans = [
    {
      id: 'individual',
      name: 'Starter',
      price: '$17',
      period: 'per month',
      description: 'For entrepreneurs and solopreneurs',
      features: [
        '14-day free trial',
        '1 website/project',
        '50 tracked keywords',
        '5 GEO tracked prompts with monthly scans',
        '2 AI engines (Google AI Overview + Perplexity)',
        'Monthly rank and AI visibility score updates',
        'Site audit covering up to 100 pages per scan',
        'Basic dashboards with 1-month historical data',
        '10 AI-assisted content suggestions',
        'Geo-target keyword tracking for local and national searches',
        'SERP feature tracking (featured snippets, Local Pack, People Also Ask)',
        'Basic PPC ad copy tracking',
        'Google Search Console & Google Analytics integrations',
        'Email alerts for ranking changes and AI visibility updates',
        'Data export in CSV and PDF formats'
      ],
      limits: {
        websites: 1,
        keywords: 50,
        contentPerMonth: 10,
        geoPrompts: 5,
        geoScanFrequency: 'monthly',
        geoEngines: 2
      },
      buttonText: 'Start 14-Day Free Trial',
      buttonStyle: 'bg-blue-600 hover:bg-blue-700',
      popular: false
    },
    {
      id: 'business',
      name: 'Business',
      price: '$39',
      period: 'per month',
      description: 'For small teams & growing businesses',
      features: [
        '14-day free trial',
        '2\u20133 websites/projects',
        '150 keyword lookups & up to 125 tracked keywords',
        '15 GEO tracked prompts with weekly scans across 3 AI engines',
        'Compare up to 5 competitor domains per project',
        'Crawl up to 500 pages per scan (5,000-page monthly audit limit)',
        'Basic backlink and citation data with 3-month history',
        '30 AI content briefs or optimizations each month',
        'Single user login with shareable reports & CSV export',
        'Standard email support',
        'Everything in Individual, plus:',
        'Multi-region keyword tracking (city, state, country)',
        'Bulk import and management of keyword lists',
        'Multi-channel reporting (SEO, PPC, AI visibility combined)',
        'Shared workspaces with comment threads on reports',
        'Slack/Microsoft Teams integration for notifications',
        'API access to sync data with your dashboards and CRM',
        'Schedule site audits and rank updates on custom cadence',
        'Access to priority roadmap feedback sessions'
      ],
      limits: {
        websites: 3,
        keywords: 125,
        contentPerMonth: 30,
        geoPrompts: 15,
        geoScanFrequency: 'weekly',
        geoEngines: 3
      },
      buttonText: 'Start 14-Day Free Trial',
      buttonStyle: 'bg-purple-600 hover:bg-purple-700',
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$79',
      period: 'per month',
      description: 'For agencies & large teams',
      features: [
        '14-day free trial',
        'Up to 10 websites/projects',
        '400 keyword lookups & 400 tracked keywords per site',
        '50 GEO tracked prompts with daily scans across 4 AI engines',
        '10 competitor domains per project',
        'Site audits up to 5,000 pages per crawl (20,000 pages/month) with JS rendering',
        'Advanced backlink and citation data with 1-year history',
        '100 AI content outlines/optimizations per month',
        '3 user seats plus view-only sharing',
        'Basic API (50 calls/day)',
        'Priority email support',
        'Scheduled PDF/CSV exports',
        'Everything in Business, plus:',
        'AI persona insights across AI engines',
        'Competitive content gap analysis and topic suggestions',
        'Dedicated onboarding specialist',
        'Advanced user permissions with role-based access control',
        'White-label portal with custom domain and branding',
        'Priority API support with SLA guarantees',
        'Quarterly strategy calls and personalized growth recommendations'
      ],
      limits: {
        websites: 10,
        keywords: 400,
        contentPerMonth: 100,
        geoPrompts: 50,
        geoScanFrequency: 'daily',
        geoEngines: 4
      },
      buttonText: 'Start 14-Day Free Trial',
      buttonStyle: 'bg-green-600 hover:bg-green-700',
      popular: false
    }
  ];

  const pricingSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "SERPfinity Pricing Plans",
    "description": "SEO and AEO platform pricing plans",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": {
          "@type": "Offer",
          "name": "Starter Plan",
          "description": "For entrepreneurs and solopreneurs. Full access to AI SEO tools.",
          "price": "17.00",
          "priceCurrency": "USD",
          "url": "https://app.serpfinity.ai/pricing"
        }
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": {
          "@type": "Offer",
          "name": "Business Plan",
          "description": "For small teams and growing businesses. Advanced analytics and multi-domain tracking.",
          "price": "39.00",
          "priceCurrency": "USD",
          "url": "https://app.serpfinity.ai/pricing"
        }
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": {
          "@type": "Offer",
          "name": "Enterprise Plan",
          "description": "For agencies and large teams. Unlimited domains, white-label reports, and priority support.",
          "price": "79.00",
          "priceCurrency": "USD",
          "url": "https://app.serpfinity.ai/pricing"
        }
      }
    ]
  };


  const PRICING_FAQ = [
    { q: "What pricing plans does SERPfinity offer?", a: "SERPfinity offers flexible pricing plans designed for businesses of every size, from solo entrepreneurs to enterprise teams. Each plan includes access to our core SEO and AEO tools with increasing limits on tracked keywords, monitored domains, site audit pages, and AI search queries. All plans include access to rank tracking, SERP analysis, keyword research, and AI visibility monitoring." },
    { q: "Is there a free trial available?", a: "Yes, SERPfinity offers a generous free trial that gives you full access to all platform features. You can track keywords, run site audits, research keywords, analyze SERPs, and monitor AI search visibility during your trial period. No credit card is required to start, so you can fully evaluate the platform risk-free before choosing a paid plan." },
    { q: "Can I upgrade or downgrade my plan at any time?", a: "Absolutely. SERPfinity allows you to change your plan at any time to match your evolving needs. Upgrades take effect immediately with prorated billing, while downgrades take effect at the start of your next billing cycle. Your data and history are preserved when switching plans, ensuring no disruption to your SEO monitoring and analysis." },
    { q: "What payment methods does SERPfinity accept?", a: "SERPfinity accepts all major credit cards including Visa, Mastercard, American Express, and Discover through our secure payment processor. We also support annual billing with significant discounts compared to monthly plans. Enterprise customers can request custom invoicing and payment terms tailored to their procurement processes." },
    { q: "Is SERPfinity worth the investment compared to other tools?", a: "SERPfinity provides exceptional value by combining tools that would otherwise require multiple subscriptions. Instead of paying separately for rank tracking, keyword research, site auditing, content optimization, and AI search monitoring, SERPfinity bundles everything into one affordable platform. Our AI search visibility features are unique and unavailable in most traditional SEO tools at any price." },
    { q: "Do you offer discounts for annual billing or agencies?", a: "Yes, SERPfinity offers significant discounts for annual billing commitments, typically saving 20 percent or more compared to monthly pricing. We also offer special agency pricing with white-label reporting options, client management features, and volume discounts for agencies managing multiple client accounts. Contact our sales team for custom enterprise and agency pricing." }
  ];

  const productOfferSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "SERPfinity SEO Platform",
    "description": "AI-Powered Search Visibility Platform with SEO and AEO tools including rank tracking, keyword research, content optimization, and AI search visibility scoring.",
    "brand": {
        "@type": "Brand",
        "name": "SERPfinity"
    },
    "url": "https://app.serpfinity.ai/pricing",
    "image": "https://app.serpfinity.ai/og-image.png",
    "offers": [
        {
            "@type": "Offer",
            "name": "Starter Plan",
            "price": "17.00",
            "priceCurrency": "USD",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://app.serpfinity.ai/pricing"
        },
        {
            "@type": "Offer",
            "name": "Business Plan",
            "price": "39.00",
            "priceCurrency": "USD",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://app.serpfinity.ai/pricing"
        },
        {
            "@type": "Offer",
            "name": "Enterprise Plan",
            "price": "79.00",
            "priceCurrency": "USD",
            "priceValidUntil": "2026-12-31",
            "availability": "https://schema.org/InStock",
            "url": "https://app.serpfinity.ai/pricing"
        }
    ],
    "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": "4.8",
        "reviewCount": "127"
    }
};
  // Schema injection via useEffect
  useEffect(() => {
    const schemas = [
      pricingSchema,
      productOfferSchema,
      {"@context":"https://schema.org","@type":"FAQPage","mainEntity":PRICING_FAQ.map(item=>({"@type":"Question","name":item.q,"acceptedAnswer":{"@type":"Answer","text":item.a}}))}
    ];
    const scriptElements = [];
    schemas.forEach(schema => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
      scriptElements.push(script);
    });
    return () => {
      scriptElements.forEach(el => el.remove());
    };
  }, []);


  return (
    <>
      <Helmet>
        <title>SERPfinity Pricing - Affordable SEO & AEO Plans Starting at $17/month</title>
        <meta name="description" content="SERPfinity pricing plans: Starter $17/mo, Business $39/mo, Enterprise $79/mo. All plans include AI Search Visibility tracking, SERP Analysis, Content Optimization, Local SEO tools and more." />
        <meta name="keywords" content="SERPfinity pricing, SEO tool pricing, AEO platform cost, AI SEO subscription, affordable SEO tools, SEO plans" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://app.serpfinity.ai/pricing" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="SERPfinity Pricing - SEO & AEO Plans Starting at $17/month" />
        <meta property="og:description" content="Choose the right plan for your business. All SERPfinity plans include AI visibility tracking, SERP analysis, content optimization, and local SEO tools." />
        <meta property="og:url" content="https://app.serpfinity.ai/pricing" />
        <meta property="og:image" content="https://thedigitalhall.club/og-image.png" />
        <meta property="og:site_name" content="SERPfinity" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SERPfinity Pricing - Starting at $17/month" />
        <meta name="twitter:description" content="Affordable AI-powered SEO and Answer Engine Optimization. Starter $17/mo, Business $39/mo, Enterprise $79/mo." />
          </Helmet>
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Choose the plan that fits your needs. All plans include access to our comprehensive AEO analysis tools.
            </p>
          </div>


        {/* Introductory Price Lock-In Banner */}
        <section aria-label="Limited-time introductory pricing offer" role="banner" className="mb-8">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-400 rounded-xl p-6 shadow-lg">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex-shrink-0 text-4xl" aria-hidden="true">&#128293;</div>
              <div className="text-center md:text-left flex-1">
                <p className="text-xl font-bold text-gray-900 mb-1">
                  Lock In Your Introductory Price Today
                </p>
                <p className="text-gray-700 text-base">
                  Our Starter plan is available at the <strong>special introductory rate of $17/month</strong> for a limited time.
                  This price will increase as we continue adding powerful new SEO and AEO features.
                  Subscribe now to keep this rate for the life of your subscription.
                </p>
              </div>
              <div className="flex-shrink-0">
                <a
                  href="/register"
                  className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold py-3 px-6 rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  aria-label="Lock in your introductory price - Start free trial now"
                >
                  Start Free Trial
                </a>
              </div>
            </div>
          </div>
        </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-white rounded-2xl shadow-lg p-8 flex flex-col ${
                  plan.popular ? 'ring-2 ring-blue-500' : 'border border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-sm font-semibold px-4 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {plan.name}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {plan.description}
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold text-gray-900">
                      {plan.price}
                    </span>
                    <span className="text-gray-600 ml-1">
                      /{plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3 mb-8 flex-grow">
                  {plan.features.map((feature, featureIndex) => {
                    const isHeader = feature.startsWith('Everything in');
                    return (
                      <li key={featureIndex} className={`flex items-start ${isHeader ? 'mt-4 pt-4 border-t border-gray-200' : ''}`}>
                        {!isHeader && (
                          <CheckIcon className="h-5 w-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                        )}
                        <span className={isHeader ? 'text-sm font-semibold text-blue-600 uppercase tracking-wide' : 'text-gray-700 text-sm'}>
                          {feature}
                        </span>
                      </li>
                    );
                  })}
                </ul>

                <button
                  onClick={() => handleSubscribe(plan.id)}
                  disabled={loading === plan.id}
                  className={`w-full py-3 px-6 rounded-lg text-white font-semibold transition-colors ${plan.buttonStyle} ${
                    loading === plan.id ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {loading === plan.id ? 'Processing...' : plan.buttonText}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
        <FAQSection items={PRICING_FAQ} />
    </>
  );
};

export default Pricing;
